import React from "react";
import { Box, Chip, Stack } from "@mui/joy";
import { QRCodeSVG } from "qrcode.react";
import CommonModal from "../../common/CommonModal";
import DarkTypography from "../../common/DarkTypography";
import DarkInput from "../../common/DarkInput";
import IconButtonCTA from "../../common/IconButtonCTA";
import { SRefresh, SSmartphone } from "../../../styles/StyledMUI";
import { useMobileDevices } from "../../../contexts/api";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";
import type {
  MobileDevice,
  MobilePairingInitiation,
} from "../../../api/mobileDevices/MobileDevice.typed";

interface PairingModalProps {
  open: boolean;
  onClose: () => void;
  /** When set, regenerates a QR for this existing device instead of creating a new one. */
  device?: MobileDevice | null;
}

const DEFAULT_LABEL = "Appareil mobile";
const PAIRING_POLL_INTERVAL_MS = 4000;

function useCountdown(target: string | null): number {
  const [secondsLeft, setSecondsLeft] = React.useState(0);

  React.useEffect(() => {
    if (!target) {
      setSecondsLeft(0);

      return;
    }

    const compute = () => {
      const diffMs = new Date(target).getTime() - Date.now();
      setSecondsLeft(Math.max(0, Math.ceil(diffMs / 1000)));
    };

    compute();
    const interval = window.setInterval(compute, 1000);

    return () => window.clearInterval(interval);
  }, [target]);

  return secondsLeft;
}

function formatSeconds(total: number): string {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export default function PairingModal({ open, onClose, device = null }: PairingModalProps) {
  const { initiatePairing, restartPairing, listDevices } = useMobileDevices();

  const isRepair = device != null;
  const repairDeviceId = device?.id ?? null;
  const [label, setLabel] = React.useState<string>(DEFAULT_LABEL);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [initiation, setInitiation] = React.useState<MobilePairingInitiation | null>(null);
  const autoStartedForId = React.useRef<number | null>(null);

  const secondsLeft = useCountdown(initiation?.expires_at ?? null);
  const expired = initiation !== null && secondsLeft === 0;

  const start = React.useCallback(async () => {
    setLoading(true);

    const result =
      isRepair && repairDeviceId != null
        ? await restartPairing(repairDeviceId)
        : await initiatePairing({ label: label.trim() || DEFAULT_LABEL });

    setLoading(false);

    if (result) setInitiation(result);
  }, [isRepair, repairDeviceId, restartPairing, initiatePairing, label]);

  React.useEffect(() => {
    if (!open) {
      setInitiation(null);
      setLabel(DEFAULT_LABEL);
      setLoading(false);
      autoStartedForId.current = null;

      return;
    }

    if (isRepair && repairDeviceId != null && autoStartedForId.current !== repairDeviceId) {
      autoStartedForId.current = repairDeviceId;
      setLabel(device?.label ?? DEFAULT_LABEL);
      void start();
    }
  }, [open, isRepair, repairDeviceId, device?.label, start]);

  React.useEffect(() => {
    if (!initiation || expired) return;

    let cancelled = false;

    const tick = async () => {
      const devices = await listDevices();
      if (cancelled) return;

      const paired = devices.find(
        (row: MobileDevice) => row.id === initiation.device_id && row.status === "paired",
      );

      if (paired) onClose();
    };

    const handle = window.setInterval(tick, PAIRING_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [initiation, expired, listDevices, onClose]);

  const qrPayload = initiation?.qr_payload ?? null;
  const qrValue = qrPayload ? JSON.stringify(qrPayload) : null;

  const showInputStep = !isRepair && !initiation;
  const confirmDisabled = showInputStep ? !label.trim() : true;
  const expiryColor = expired ? "danger" : secondsLeft < 60 ? "warning" : "success";

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={isRepair ? "Réappairer l'appareil" : "Appairer un appareil mobile"}
      size="md"
      onConfirm={showInputStep ? start : undefined}
      confirmLabel="Générer le QR"
      submitDisabled={confirmDisabled}
      loading={loading}
      fitContentHeight
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            padding: 1.5,
            borderRadius: 8,
            backgroundColor: withAlpha(colorTokens.accent.primary, 0.08),
            border: `1px solid ${withAlpha(colorTokens.accent.primary, 0.18)}`,
          }}
        >
          <SSmartphone sx={{ fontSize: 24, color: colorTokens.accent.primary, flexShrink: 0 }} />
          <DarkTypography level="body-sm" colorVariant="primary">
            Ouvrez l'app sur le téléphone et scannez ce QR code.
          </DarkTypography>
        </Box>

        {showInputStep ? (
          <Box sx={{ display: "grid", gap: 1 }}>
            <DarkTypography level="body-xs" colorVariant="muted">
              Nom de l'appareil
            </DarkTypography>
            <DarkInput
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={DEFAULT_LABEL}
              size="sm"
              disabled={loading}
            />
          </Box>
        ) : null}

        {initiation && qrValue ? (
          <Stack alignItems="center" spacing={1.5}>
            <Box
              sx={{
                padding: 2,
                backgroundColor: "#ffffff",
                borderRadius: 12,
                opacity: expired ? 0.35 : 1,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              <QRCodeSVG value={qrValue ?? ""} size={220} level="M" includeMargin={false} />
            </Box>

            <Chip size="sm" color={expiryColor} variant="soft">
              {expired ? "QR expiré" : `Expire dans ${formatSeconds(secondsLeft)}`}
            </Chip>

            <IconButtonCTA
              aria-label="Générer un nouveau QR"
              ctaVariant="accent"
              variant="plain"
              onClick={start}
              disabled={loading}
            >
              <SRefresh />
            </IconButtonCTA>
          </Stack>
        ) : null}
      </Stack>
    </CommonModal>
  );
}
